// src/components/PatientDetails/SharePatientModal.tsx
// ---------------------------------------------------------------------------
// Modal for sharing a patient with another platform user.
// Includes user search autocomplete and optional environment selection.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { fetchData, createData } from "api/apiHelpers";
import { useNotification } from "hooks/useNotification";
import logger from "utils/logger";
import { useTranslation } from "../../i18n";
import {
  athenaBackdropSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkTitleSx,
} from "../layout/AthenaDialogStyles";
import { glass } from "../../theme/glassTokens";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SearchUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface SharePatientModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | number;
  patientName?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SharePatientModal: React.FC<SharePatientModalProps> = ({
  open,
  onClose,
  patientId,
  patientName,
}) => {
  const { t } = useTranslation();
  const notification = useNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [sharing, setSharing] = useState(false);

  // Search users with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchData<SearchUser[]>(
          `/auth/users/search/?q=${encodeURIComponent(searchQuery)}`
        );
        setOptions(results);
      } catch (err) {
        logger.error("[SharePatient] Search failed:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleShare = useCallback(async () => {
    if (!selectedUser) return;
    setSharing(true);
    try {
      await createData(`/patients/${patientId}/share/`, {
        user_id: selectedUser.id,
      });
      notification.success(
        `Patient shared with ${selectedUser.email}`
      );
      onClose();
    } catch (err: any) {
      logger.error("[SharePatient] Failed:", err);
      notification.error(t("share_patient.failed", "Failed to share patient"));
    } finally {
      setSharing(false);
    }
  }, [selectedUser, patientId, notification, onClose]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setSearchQuery("");
      setOptions([]);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ backdrop: { sx: athenaBackdropSx } }}
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle sx={{ ...athenaDarkTitleSx, gap: 1 }}>
        <PersonAddIcon />
        Share Patient
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={athenaDarkContentSx}>
        <Typography variant="body2" color={glass.textSecondary} sx={{ mb: 2 }}>
          {patientName
            ? t("share_patient.description_named", "Share \"{name}\" with another user. They will receive a notification.").replace("{name}", patientName)
            : t("share_patient.description_generic", "Share this patient with another user. They will receive a notification.")}
        </Typography>

        <Autocomplete
          options={options}
          getOptionLabel={(o) =>
            `${o.email}${o.first_name ? ` (${o.first_name} ${o.last_name || ""})` : ""}`
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedUser}
          onChange={(_, val) => setSelectedUser(val)}
          onInputChange={(_, val) => setSearchQuery(val)}
          loading={loading}
          noOptionsText={searchQuery.length < 2 ? t("share_patient.search_hint", "Type to search users...") : t("share_patient.no_users_found", "No users found")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("share_patient.search_label", "Search user by email or name")}
              placeholder={t("share_patient.search_placeholder", "email@example.com")}
              autoFocus
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.email}</Typography>
                {option.first_name && (
                  <Typography variant="caption" color="text.secondary">
                    {option.first_name} {option.last_name || ""}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={!selectedUser || sharing}
          startIcon={sharing ? <CircularProgress size={16} /> : <PersonAddIcon />}
        >
          {sharing ? t("share_patient.sharing", "Sharing...") : t("action.share", "Share")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePatientModal;
